import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import projectService from '../services/projectService';

/**
 * Who can work in this project, and how somebody else gets in.
 *
 * Codes rather than email invitations: nothing to deliver, nothing to land in
 * spam. The code carries the role, so what someone gets is decided before it is
 * handed over rather than after they are already in.
 */

const Container = styled.div`
  max-width: 820px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.05rem;
  margin: 0.75rem 0 2rem;
  line-height: 1.6;
  max-width: 60ch;
`;

const Panel = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.35rem 1.5rem;
  margin-bottom: 1.25rem;
`;

const PanelTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1rem;
  color: var(--text-primary);
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.7rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);

  &:first-of-type { border-top: none; }
`;

const Who = styled.div`
  flex: 1;
  min-width: 0;
`;

const Email = styled.div`
  color: var(--text-primary);
  font-size: 0.94rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Joined = styled.div`
  color: var(--text-muted);
  font-size: 0.78rem;
  margin-top: 0.15rem;
`;

const RoleChip = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(p) => (p.$owner ? 'rgba(34,211,238,0.12)' : 'rgba(148,163,184,0.12)')};
  color: ${(p) => (p.$owner ? 'var(--accent-cyan)' : '#94a3b8')};
  border: 1px solid ${(p) => (p.$owner ? 'rgba(34,211,238,0.35)' : 'rgba(148,163,184,0.3)')};
`;

const Button = styled.button`
  border-radius: var(--radius-md);
  padding: 0.55rem 1.05rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  background: ${(p) => (p.$primary ? 'var(--accent-cyan)' : 'transparent')};
  color: ${(p) => (p.$primary ? 'var(--primary-bg)' : 'var(--text-secondary)')};
  border-color: ${(p) => (p.$primary ? 'transparent' : 'var(--border-color)')};

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { filter: brightness(1.1); }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.5rem;
`;

const Select = styled.select`
  background: rgba(22, 26, 43, 0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.55rem 0.8rem;
  color: var(--text-primary);
  font-size: 0.88rem;

  option { background: var(--primary-bg); color: var(--text-primary); }
`;

const CodeBox = styled.div`
  border: 1px dashed rgba(34, 211, 238, 0.5);
  background: rgba(34, 211, 238, 0.06);
  border-radius: var(--radius-md);
  padding: 1rem;
  text-align: center;
  margin-top: 0.9rem;
`;

const CodeValue = styled.div`
  font-family: var(--font-mono, monospace);
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  color: var(--accent-cyan);
`;

const CodeMeta = styled.div`
  color: var(--text-muted);
  font-size: 0.76rem;
  margin-top: 0.4rem;
`;

const Message = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  color: var(--text-secondary);
`;

const Muted = styled.span`
  color: var(--text-muted);
`;

const ROLE_LABEL = { owner: 'Owner', approver: 'Can approve', viewer: 'Read only' };

function People() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [role, setRole] = useState('approver');
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);

  const projectId = projectService.current();

  const load = useCallback(async () => {
    if (!projectId) { setError('Pick a project first.'); return; }
    try {
      setError('');
      setData(await projectService.members(projectId));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load the people in this project');
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const makeCode = async () => {
    try {
      setBusy(true);
      setError('');
      setFresh(await projectService.invite(projectId, role));
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not create an invite');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (code) => {
    try {
      await projectService.revokeInvite(projectId, code);
      if (fresh && fresh.code === code) setFresh(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not cancel that code');
    }
  };

  const remove = async (member) => {
    if (!window.confirm(`Remove ${member.email || 'this person'} from the project?`)) return;
    try {
      await projectService.removeMember(projectId, member.userId);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not remove them');
    }
  };

  if (!data && !error) return <Container><Message>Loading…</Message></Container>;

  const isOwner = data && data.yourRole === 'owner';

  return (
    <Container>
      <Title>People</Title>
      <Subtitle>
        Who can work in this project. Invites are codes — read one out or paste it
        into a message; there is no email to wait for.
      </Subtitle>

      {error && <Panel style={{ borderColor: 'rgba(248,113,113,0.4)' }}>{error}</Panel>}

      {data && (
        <>
          <Panel>
            <PanelTitle>Members</PanelTitle>
            {data.members.map((m) => (
              <Row key={m.userId}>
                <Who>
                  <Email>{m.email || m.name || m.userId}</Email>
                  <Joined>Joined {m.joinedAt ? new Date(m.joinedAt).toLocaleDateString() : 'at the start'}</Joined>
                </Who>
                <RoleChip $owner={m.role === 'owner'}>{ROLE_LABEL[m.role] || m.role}</RoleChip>
                {isOwner && m.role !== 'owner' && (
                  <Button onClick={() => remove(m)}>Remove</Button>
                )}
              </Row>
            ))}
          </Panel>

          {isOwner && (
            <Panel>
              <PanelTitle>Invite someone</PanelTitle>
              <Controls>
                <Select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="approver">Can approve — run campaigns and approve sends</option>
                  <option value="viewer">Read only — can look, cannot send</option>
                </Select>
                <Button $primary onClick={makeCode} disabled={busy}>
                  {busy ? 'Creating…' : 'Create a code'}
                </Button>
              </Controls>

              {fresh && (
                <CodeBox>
                  <CodeValue>{fresh.code}</CodeValue>
                  <CodeMeta>
                    Joins as {ROLE_LABEL[fresh.role]} · one use ·
                    expires {new Date(fresh.expiresAt).toLocaleDateString()}
                  </CodeMeta>
                </CodeBox>
              )}
            </Panel>
          )}

          {data.invites.length > 0 && (
            <Panel>
              <PanelTitle>Codes not used yet</PanelTitle>
              {data.invites.map((i) => (
                <Row key={i.code}>
                  <Who>
                    <Email style={{ letterSpacing: '0.1em' }}>{i.code}</Email>
                    <Joined>
                      Joins as {ROLE_LABEL[i.role]} · expires {new Date(i.expiresAt).toLocaleDateString()}
                    </Joined>
                  </Who>
                  {isOwner && <Button onClick={() => revoke(i.code)}>Cancel</Button>}
                </Row>
              ))}
            </Panel>
          )}

          {!isOwner && (
            <Panel><Muted>Only an owner can invite people or change who has access.</Muted></Panel>
          )}
        </>
      )}
    </Container>
  );
}

export default People;
